import React from 'react';
import { Tabs, Descriptions, Spin, Card, Table, Result, Row, Col, Typography } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchSqlData } from '../../../services/api';
import type { AdmVehicle, ItModule, VwPersonnel } from '../../../types';
import { ArrowLeftOutlined, CarOutlined, ToolOutlined, SafetyCertificateOutlined, AppstoreAddOutlined, HistoryOutlined } from '@ant-design/icons';
import { useGetPersonnel } from '../../../hooks/genericHooks';
import Button from '../../../components/common/Button';

interface VehicleMaintenance {
  id: number;
  vehicle_id: number;
  maintenance_date: string;
  description: string;
  created_by?: string;
}
interface VehicleInspection {
  id: number;
  vehicle_id: number;
  inspection_date: string;
  description: string;
  created_by?: string;
}
interface VehicleTire {
  id: number;
  vehicle_id: number;
  tire_status: string;
  description: string;
  checked_at: string;
  created_by?: string;
}
interface VehicleRental {
  id: number;
  vehicle_id: number;
  rental_start: string;
  rental_end: string | null;
  contract_end: string | null;
  company_name: string;
  created_by?: string;
}
interface VehicleAssignment {
  id: number;
  vehicle_id: number;
  user_id: number;
  assigned_at: string;
  released_at: string | null;
  note?: string;
  created_by?: string;
}

const formatDate = (date?: string | null, locale = 'tr-TR') => {
  if (!date) return '-';
  try {
    return new Date(date).toLocaleString(locale, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return date;
  }
};

const VEHICLE_ENUM_LABELS: Record<string, string> = {
  CompanyOwned: 'Company Owned',
  Rented: 'Rented',
  Leased: 'Leased',
  Available: 'Available',
  Assigned: 'Assigned',
  InMaintenance: 'In Maintenance',
  OutOfService: 'Out of Service',
  Inactive: 'Inactive',
  Gasoline: 'Gasoline',
  Diesel: 'Diesel',
  LPG: 'LPG',
  Electric: 'Electric',
  Hybrid: 'Hybrid',
};

const MecVehicleDetail: React.FC = () => {
  const locale = 'en-US';
  const translateEnum = (value?: string | null) => {
    if (!value) return '-';
    return VEHICLE_ENUM_LABELS[value] || String(value);
  };
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const vehicleId = params.id ? parseInt(params.id, 10) : undefined;

  const { data: vehicle, isLoading: isLoadingVehicle, isError: isErrorVehicle, error: errorVehicle } = useQuery<AdmVehicle, Error>({
    queryKey: ['machineryVehicleDetail', vehicleId],
    queryFn: async () => {
      if (!vehicleId) throw new Error('No valid record number was found in the URL.');
      const rows = await fetchSqlData('adm_vehicles', 'id', vehicleId);
      if (!Array.isArray(rows) || !rows.length) throw new Error('Vehicle Not Found');
      return rows[0];
    },
    enabled: !!vehicleId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: maintenances = [], isLoading: isLoadingMaintenances } = useQuery<VehicleMaintenance[], Error>({
    queryKey: ['machVehicleMaintenances', vehicleId],
    queryFn: () => fetchSqlData('adm_vehicle_maintenance', 'vehicle_id', vehicleId),
    enabled: !!vehicleId,
  });
  const { data: inspections = [], isLoading: isLoadingInspections } = useQuery<VehicleInspection[], Error>({
    queryKey: ['machVehicleInspections', vehicleId],
    queryFn: () => fetchSqlData('adm_vehicle_inspection', 'vehicle_id', vehicleId),
    enabled: !!vehicleId,
  });
  const { data: tires = [], isLoading: isLoadingTires } = useQuery<VehicleTire[], Error>({
    queryKey: ['machVehicleTires', vehicleId],
    queryFn: () => fetchSqlData('adm_vehicle_tires', 'vehicle_id', vehicleId),
    enabled: !!vehicleId,
  });
  const { data: rentals = [], isLoading: isLoadingRentals } = useQuery<VehicleRental[], Error>({
    queryKey: ['machVehicleRentals', vehicleId],
    queryFn: () => fetchSqlData('adm_vehicle_rental', 'vehicle_id', vehicleId),
    enabled: !!vehicleId,
  });
  const { data: assignments = [], isLoading: isLoadingAssignments } = useQuery<VehicleAssignment[], Error>({
    queryKey: ['machVehicleAssignments', vehicleId],
    queryFn: () => fetchSqlData('adm_vehicle_assignments', 'vehicle_id', vehicleId),
    enabled: !!vehicleId,
  });

  const { data: departmentsData = [] } = useQuery<ItModule[], Error>({
    queryKey: ['departments'],
    queryFn: () => fetchSqlData('it_modules'),
    staleTime: Infinity,
  });
  const { data: personnelResp } = useGetPersonnel();
  const personnelData: VwPersonnel[] = Array.isArray(personnelResp) ? personnelResp : ((personnelResp as any)?.data || []);

  const departmentsMap = React.useMemo(() => {
    const map: Record<number, string> = {};
    departmentsData.forEach(d => {
      // @ts-ignore
      if (d?.id) map[d.id as any] = (d as any).name || 'Unknown User';
    });
    return map;
  }, [departmentsData]);
  const usersMap = React.useMemo(() => {
    const map: Record<number, string> = {};
    personnelData.forEach(u => {
      if (u?.id) map[u.id] = `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Unknown User';
    });
    return map;
  }, [personnelData]);

  if (!vehicleId) {
    return (
      <Result
        status="error"
        title={'Invalid Vehicle ID'}
        subTitle={'No valid record number was found in the URL.'}
        extra={<Button type="primary" onClick={() => navigate(-1)}>{'Back'}</Button>}
      />
    );
  }
  if (isLoadingVehicle) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (isErrorVehicle) {
    return (
      <Result
        status="error"
        title={'Vehicle Detail Could Not Be Loaded'}
        subTitle={(errorVehicle as Error)?.message || 'Unknown error'}
        extra={<Button type="primary" onClick={() => navigate(-1)}>{'Back'}</Button>}
      />
    );
  }
  if (!vehicle) {
    return (
      <Result
        status="warning"
        title={'Vehicle Not Found'}
        subTitle={`Vehicle ID: ${vehicleId}`}
        extra={<Button type="primary" onClick={() => navigate(-1)}>{'Back'}</Button>}
      />
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            {'Back'}
          </Button>
        </Col>
        <Col flex="auto">
          <Typography.Title level={3} style={{ margin: 0 }}>
            <CarOutlined style={{ marginRight: 8 }} />
            {`Vehicle Detail: ${vehicle.license_plate}`}
          </Typography.Title>
        </Col>
      </Row>
      <Card bordered>
        <Tabs
          defaultActiveKey="detail"
          tabBarGutter={16}
          items={[
            {
              key: 'detail',
              label: <>{'Vehicle Info'}</>,
              children: (
                <Descriptions bordered column={1}>
                  <Descriptions.Item label={'License Plate'}>{vehicle.license_plate || '-'}</Descriptions.Item>
                  <Descriptions.Item label={'Make'}>{vehicle.make || '-'}</Descriptions.Item>
                  <Descriptions.Item label={'Model'}>{vehicle.model || '-'}</Descriptions.Item>
                  <Descriptions.Item label={'Year'}>{vehicle.year ?? '-'}</Descriptions.Item>
                  <Descriptions.Item label={'Kilometer'}>{vehicle.kilometer?.toLocaleString(locale) || '-'}</Descriptions.Item>
                  <Descriptions.Item label={'Fuel'}>{translateEnum(vehicle.fuel_type)}</Descriptions.Item>
                  <Descriptions.Item label={'Ownership'}>{translateEnum(vehicle.ownership_type)}</Descriptions.Item>
                  <Descriptions.Item label={'Vehicle Type'}>{vehicle.machine_type || '-'}</Descriptions.Item>
                  <Descriptions.Item label={'Hours Counter'}>{vehicle.hours_counter?.toLocaleString(locale) || '-'}</Descriptions.Item>
                  <Descriptions.Item label={'Seat Count'}>{vehicle.seats?.toLocaleString(locale) || '-'}</Descriptions.Item>
                  <Descriptions.Item label={'Passenger Capacity'}>{vehicle.passenger_capacity?.toLocaleString(locale) || '-'}</Descriptions.Item>
                  <Descriptions.Item label={'User'}>{usersMap[vehicle.operator_id as number] || '-'}</Descriptions.Item>
                  <Descriptions.Item label={'Status'}>{translateEnum(vehicle.status)}</Descriptions.Item>
                </Descriptions>
              ),
            },
            {
              key: 'maintenance',
              label: <><ToolOutlined /> {'Maintenance Records'}</>,
              children: (
                <Table<VehicleMaintenance>
                  loading={isLoadingMaintenances}
                  dataSource={maintenances}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  size="middle"
                  columns={[
                    { title: 'Date', dataIndex: 'maintenance_date', key: 'maintenance_date', render: (d) => formatDate(d, locale) },
                    { title: 'Description', dataIndex: 'description', key: 'description' },
                    { title: 'Created By', dataIndex: 'created_by', key: 'created_by', render: (u) => usersMap[Number(u)] || '-' },
                  ]}
                />
              ),
            },
            {
              key: 'inspection',
              label: <><SafetyCertificateOutlined /> {'Inspection Records'}</>,
              children: (
                <Table<VehicleInspection>
                  loading={isLoadingInspections}
                  dataSource={inspections}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  size="middle"
                  columns={[
                    { title: 'Date', dataIndex: 'inspection_date', key: 'inspection_date', render: (d) => formatDate(d, locale) },
                    { title: 'Description', dataIndex: 'description', key: 'description' },
                    { title: 'Created By', dataIndex: 'created_by', key: 'created_by', render: (u) => usersMap[Number(u)] || '-' },
                  ]}
                />
              ),
            },
            {
              key: 'tire',
              label: <><AppstoreAddOutlined /> {'Tire Records'}</>,
              children: (
                <Table<VehicleTire>
                  loading={isLoadingTires}
                  dataSource={tires}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  size="middle"
                  columns={[
                    { title: 'Status', dataIndex: 'tire_status', key: 'tire_status', render: (s) => translateEnum(s) },
                    { title: 'Description', dataIndex: 'description', key: 'description' },
                    { title: 'Checked At', dataIndex: 'checked_at', key: 'checked_at', render: (d) => formatDate(d, locale) },
                    { title: 'Created By', dataIndex: 'created_by', key: 'created_by', render: (u) => usersMap[Number(u)] || '-' },
                  ]}
                />
              ),
            },
            {
              key: 'rental',
              label: <>{'Rental Records'}</>,
              children: (
                <Table<VehicleRental>
                  loading={isLoadingRentals}
                  dataSource={rentals}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  size="middle"
                  columns={[
                    { title: 'Start', dataIndex: 'rental_start', key: 'rental_start', render: (d) => formatDate(d, locale) },
                    { title: 'End', dataIndex: 'rental_end', key: 'rental_end', render: (d) => formatDate(d, locale) },
                    { title: 'Contract End', dataIndex: 'contract_end', key: 'contract_end', render: (d) => formatDate(d, locale) },
                    { title: 'Company', dataIndex: 'company_name', key: 'company_name' },
                    { title: 'Created By', dataIndex: 'created_by', key: 'created_by', render: (u) => usersMap[Number(u)] || '-' },
                  ]}
                />
              ),
            },
            {
              key: 'assignment',
              label: <><HistoryOutlined /> {'Assignment History'}</>,
              children: (
                <Table<VehicleAssignment>
                  loading={isLoadingAssignments}
                  dataSource={assignments}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  size="middle"
                  columns={[
                    { title: 'User', dataIndex: 'user_id', key: 'user_id', render: (u) => usersMap[Number(u)] || '-' },
                    { title: 'Start', dataIndex: 'assigned_at', key: 'assigned_at', render: (d) => formatDate(d, locale) },
                    { title: 'End', dataIndex: 'released_at', key: 'released_at', render: (d) => formatDate(d, locale) },
                    { title: 'Note', dataIndex: 'note', key: 'note' },
                  ]}
                />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default MecVehicleDetail;
